import { useMutation } from '@tanstack/react-query';
import { createRealm } from '../services';
import { useSelector } from 'react-redux';
import { selectAuthToken } from 'selectors';
import { AxiosResponse } from 'axios';
import {
  CreateRealmErrorResponse,
  CreateRealmParams,
  CreateRealmSuccessResponse,
} from 'SandboxModule/types';
import useSandboxes, { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import { eventManager } from 'core/event/eventManager';
import { AppEvents } from 'core/event/constants';
import { useNotification } from 'hooks/useNotification';
import ApiClientManager from 'core/apiClient';

export const useCreateRealm = ({ onSuccess }: { onSuccess?: VoidFunction }) => {
  const token = useSelector(selectAuthToken);
  const { fetchSandboxes } = useSandboxes();
  const { showSuccess, showError } = useNotification();

  const mutation = useMutation<
    AxiosResponse<CreateRealmSuccessResponse>,
    AxiosResponse<CreateRealmErrorResponse>,
    CreateRealmParams
  >({
    mutationFn: async (params) => {
      if (!token) {
        throw new Error('No auth token available');
      }
      const config = params.tenantName
        ? { headers: ApiClientManager.buildUrlHeaders({ tenantName: params.tenantName }) }
        : {};
      return createRealm({
        params,
        token,
        config,
      }) as Promise<AxiosResponse<CreateRealmSuccessResponse>>;
    },
    onSuccess: ({ data }) => {
      onSuccess?.();
      eventManager.emit({
        eventName: AppEvents.CreateRealmSuccess,
        detail: { data },
      });

      if (data) showSuccess(data);

      fetchSandboxes(SandboxIsoStateIds.SandboxCreationTimer);
      fetchSandboxes(SandboxIsoStateIds.AppbarRealmSelector);
    },
    onError: (error) => {
      showError(`${error.data.message || 'Failed to create'}`);
    },
  });

  return {
    createRealm: mutation.mutate,
    isCreatingRealm: mutation.isPending,
  };
};
